import React, { useState } from "react";
import { toast } from "@/lib/toast";
import { Check, Pencil, Plus, RotateCcw, Trash2, X } from "lucide-react";
import TopBar from "../components/TopBar";
import { useConfirm } from "../components/ConfirmDialog";
import { useWeeklyPlan, DAY_KEYS, DAY_LABELS } from "../lib/weekly-plan";
import { useDayTypeSchedule } from "../lib/day-type-schedule";

// ── Day accent palette (for WeekSection) ─────────────────────────────────────
const DAY_ABBREV = {
  domingo: "DOM", lunes: "LUN", martes: "MAR", miercoles: "MIÉ",
  jueves: "JUE", viernes: "VIE", sabado: "SÁB",
};
const DAY_COLORS = {
  domingo:   { bg: "bg-rose-50 dark:bg-rose-900/20",       text: "text-rose-500 dark:text-rose-400"      },
  lunes:     { bg: "bg-blue-50 dark:bg-blue-900/20",       text: "text-blue-500 dark:text-blue-400"      },
  martes:    { bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-500 dark:text-emerald-400" },
  miercoles: { bg: "bg-violet-50 dark:bg-violet-900/20",   text: "text-violet-500 dark:text-violet-400"  },
  jueves:    { bg: "bg-amber-50 dark:bg-amber-900/20",     text: "text-amber-500 dark:text-amber-400"    },
  viernes:   { bg: "bg-teal-50 dark:bg-teal-900/20",       text: "text-teal-500 dark:text-teal-400"      },
  sabado:    { bg: "bg-indigo-50 dark:bg-indigo-900/20",   text: "text-indigo-500 dark:text-indigo-400"  },
};

// ── Day-type defaults (v2 array format) ───────────────────────────────────────
const FERIADO_DEFAULTS = [
  { id: "f1", time: "6:00 AM",  activity: "Devocional personal"                 },
  { id: "f2", time: "8:00 AM",  activity: "Desayuno con la familia"             },
  { id: "f3", time: "10:00 AM", activity: "Actividades personales / recreación" },
  { id: "f4", time: "1:00 PM",  activity: "Almuerzo y descanso"                 },
  { id: "f5", time: "4:00 PM",  activity: "Tiempo libre / paseo"                },
  { id: "f6", time: "8:00 PM",  activity: "Familia / descanso nocturno"         },
];
const HABIL_DEFAULTS = [
  { id: "h1", time: "6:00 AM",  activity: "Devocional y oración"                },
  { id: "h2", time: "8:00 AM",  activity: "Visitas pastorales"                  },
  { id: "h3", time: "10:00 AM", activity: "Estudios bíblicos / seguimiento"     },
  { id: "h4", time: "1:00 PM",  activity: "Almuerzo"                            },
  { id: "h5", time: "3:00 PM",  activity: "Administración y correspondencia"    },
  { id: "h6", time: "7:00 PM",  activity: "Culto o reunión de iglesia"          },
];
const DOMINGO_DEFAULTS = [
  { id: "d1", time: "6:00 AM",  activity: "Preparación personal"                },
  { id: "d2", time: "9:00 AM",  activity: "Escuela Sabática / clubes"           },
  { id: "d3", time: "11:00 AM", activity: "Culto de adoración"                  },
  { id: "d4", time: "1:00 PM",  activity: "Confraternización / almuerzo"        },
  { id: "d5", time: "3:00 PM",  activity: "Actividades juveniles o especiales"  },
  { id: "d6", time: "6:00 PM",  activity: "Actividades vespertinas / evangelismo"},
];

// ── Day-type configuration (7-day era) ───────────────────────────────────────
const DAY_TYPES = [
  { id: "domingo",   label: "Dom", accentText: "text-rose-500 dark:text-rose-400",       defaults: DOMINGO_DEFAULTS  },
  { id: "lunes",     label: "Lun", accentText: "text-blue-500 dark:text-blue-400",       defaults: FERIADO_DEFAULTS  },
  { id: "martes",    label: "Mar", accentText: "text-emerald-500 dark:text-emerald-400", defaults: HABIL_DEFAULTS    },
  { id: "miercoles", label: "Mié", accentText: "text-violet-500 dark:text-violet-400",   defaults: HABIL_DEFAULTS    },
  { id: "jueves",    label: "Jue", accentText: "text-amber-500 dark:text-amber-400",     defaults: HABIL_DEFAULTS    },
  { id: "viernes",   label: "Vie", accentText: "text-teal-500 dark:text-teal-400",       defaults: HABIL_DEFAULTS    },
  { id: "sabado",    label: "Sáb", accentText: "text-indigo-500 dark:text-indigo-400",   defaults: []                },
];

// ── Timeline block ────────────────────────────────────────────────────────────
function TimelineBlock({ block, accentText, isLast, onUpdate, onRemove }) {
  const [editingTime,     setEditingTime]     = useState(!block.time);
  const [editingActivity, setEditingActivity] = useState(false);
  const [timeDraft,       setTimeDraft]       = useState(block.time);
  const [actDraft,        setActDraft]        = useState(block.activity);

  const commitTime = () => {
    const v = timeDraft.trim();
    onUpdate({ time: v }, !!v);
    setEditingTime(false);
  };

  const commitActivity = () => {
    onUpdate({ activity: actDraft.trim() });
    setEditingActivity(false);
  };

  const startActivity = () => {
    setActDraft(block.activity);
    setEditingActivity(true);
  };

  return (
    <div className="flex gap-3">

      {/* Time column */}
      <div className="flex flex-col items-center shrink-0" style={{ width: 72 }}>
        {editingTime ? (
          <input
            autoFocus
            value={timeDraft}
            onChange={e => setTimeDraft(e.target.value)}
            onBlur={commitTime}
            onKeyDown={e => {
              if (e.key === "Enter")  commitTime();
              if (e.key === "Escape") { setTimeDraft(block.time); setEditingTime(false); }
            }}
            placeholder="6:00 AM"
            className={`w-full bg-slate-100 dark:bg-slate-700 rounded-[7px] px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-center outline-none ${accentText}`}
          />
        ) : (
          <button
            onClick={() => { setTimeDraft(block.time); setEditingTime(true); }}
            className={`bg-slate-100/80 dark:bg-slate-700/60 rounded-[7px] px-1.5 py-0.5 text-[11px] font-bold tabular-nums whitespace-nowrap transition-colors duration-150 hover:bg-slate-200/70 dark:hover:bg-slate-700
              ${block.time ? accentText : "text-slate-300 dark:text-slate-600"}`}
          >
            {block.time || "- - -"}
          </button>
        )}

        {!isLast && (
          <div className="flex-1 w-px bg-slate-100 dark:bg-slate-700/40 my-1.5 min-h-[12px]" />
        )}
      </div>

      {/* Activity card */}
      <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-2"}`}>
        <div className="bg-white dark:bg-slate-800 rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 px-3 py-2.5 flex items-center gap-1.5 shadow-[0_1px_4px_rgba(0,0,0,0.025)]">

          {editingActivity ? (
            <>
              <input
                autoFocus
                value={actDraft}
                onChange={e => setActDraft(e.target.value)}
                onBlur={commitActivity}
                onKeyDown={e => {
                  if (e.key === "Enter")  commitActivity();
                  if (e.key === "Escape") setEditingActivity(false);
                }}
                placeholder="Actividad para este horario..."
                className="flex-1 bg-transparent text-[13px] font-medium text-slate-800 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 outline-none border-b border-slate-200 dark:border-slate-600 pb-0.5"
              />
              <div className="flex gap-1 shrink-0 ml-1">
                <button
                  onClick={commitActivity}
                  className="h-7 w-7 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 active:scale-[0.93] transition-all duration-150"
                >
                  <Check size={12} />
                </button>
                <button
                  onClick={() => setEditingActivity(false)}
                  className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400 active:scale-[0.93] transition-all duration-150"
                >
                  <X size={12} />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="flex-1 min-w-0 text-[13px] font-medium text-slate-800 dark:text-slate-100 truncate">
                {block.activity || (
                  <span className="text-slate-300 dark:text-slate-500 italic font-normal text-[12px]">
                    Sin actividad
                  </span>
                )}
              </p>
              <button
                onClick={startActivity}
                className="h-7 w-7 rounded-full flex items-center justify-center text-slate-300 dark:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-500 dark:hover:text-slate-400 transition-colors duration-150 shrink-0"
              >
                <Pencil size={12} />
              </button>
              <button
                onClick={onRemove}
                className="h-7 w-7 rounded-full flex items-center justify-center text-slate-200 dark:text-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-400 dark:hover:text-red-400 transition-colors duration-150 shrink-0"
              >
                <Trash2 size={11} />
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Day-type panel ────────────────────────────────────────────────────────────
function DayTypePanel({ dayType, confirm }) {
  const { blocks, addBlock, updateBlock, removeBlock, resetBlocks } = useDayTypeSchedule(dayType);

  const handleReset = async () => {
    const ok = await confirm({
      title:        `¿Restaurar ${DAY_LABELS[dayType.id] ?? dayType.label}?`,
      description:  "Se volverán a los valores por defecto.",
      confirmLabel: "Restaurar",
    });
    if (!ok) return;
    resetBlocks();
    toast.success("Horario restaurado");
  };

  return (
    <div>
      {blocks.length > 0 ? (
        <div>
          {blocks.map((block, i) => (
            <TimelineBlock
              key={block.id}
              block={block}
              accentText={dayType.accentText}
              isLast={i === blocks.length - 1}
              onUpdate={(changes, resort) => updateBlock(block.id, changes, resort)}
              onRemove={() => removeBlock(block.id)}
            />
          ))}
        </div>
      ) : (
        <p className="text-[13px] text-slate-400 dark:text-slate-500 text-center py-4 italic">
          Sin bloques. Agrega uno para comenzar.
        </p>
      )}

      {/* Add block */}
      <button
        onClick={addBlock}
        className="w-full mt-3 flex items-center justify-center gap-1.5 py-2.5 rounded-[14px] border border-dashed border-slate-200 dark:border-slate-700 text-[12px] font-medium text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-500 dark:hover:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 active:scale-[0.99] active:opacity-80"
      >
        <Plus size={13} />
        Agregar bloque
      </button>

      {/* Reset */}
      <button
        onClick={handleReset}
        className="w-full flex items-center justify-center gap-1.5 py-3 mt-1 text-[11px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-150"
      >
        <RotateCcw size={11} />
        Restaurar valores por defecto
      </button>
    </div>
  );
}

// ── Horario Diario section ────────────────────────────────────────────────────
const DAY_KEYS_7 = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

function DailySection({ confirm }) {
  const [activeId, setActiveId] = useState(() => DAY_KEYS_7[new Date().getDay()]);
  const currentType = DAY_TYPES.find(t => t.id === activeId);

  return (
    <div className="space-y-4">
      <p className="text-[12px] text-slate-500 dark:text-slate-400 ml-1">
        Define la estructura de tu día. Toca la hora para editar — los bloques se ordenan automáticamente.
      </p>

      {/* 7-day selector */}
      <div className="flex gap-1">
        {DAY_TYPES.map(t => {
          const isActive = activeId === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveId(t.id)}
              className={`flex-1 py-2.5 rounded-[12px] border transition-all duration-200 ease-out active:scale-[0.97]
                ${isActive
                  ? "bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-600/60 shadow-[0_2px_10px_rgba(0,0,0,0.06)]"
                  : "bg-slate-50/60 dark:bg-slate-800/40 border-transparent hover:bg-white/80 dark:hover:bg-slate-800/70"
                }`}
            >
              <p className={`text-[11px] font-bold leading-tight transition-colors duration-150 ${isActive ? t.accentText : "text-slate-400 dark:text-slate-500"}`}>
                {t.label}
              </p>
            </button>
          );
        })}
      </div>

      {/* Active day timeline */}
      {currentType && (
        <DayTypePanel key={activeId} dayType={currentType} confirm={confirm} />
      )}
    </div>
  );
}

// ── Mi Semana section ─────────────────────────────────────────────────────────
function WeekSection({ confirm }) {
  const { plan, update: updatePlan, reset: resetPlan } = useWeeklyPlan();
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");

  const startEdit  = (key) => { setEditingKey(key); setDraft(plan[key] || ""); };
  const commitEdit = (key) => {
    if (editingKey !== key) return;
    updatePlan({ ...plan, [key]: draft.trim() });
    setEditingKey(null);
  };
  const cancelEdit = () => setEditingKey(null);

  const handleReset = async () => {
    const ok = await confirm({
      title:        "¿Restaurar plan semanal?",
      description:  "Se volverán a los valores por defecto.",
      confirmLabel: "Restaurar",
    });
    if (!ok) return;
    resetPlan();
    setEditingKey(null);
    toast.success("Plan semanal restaurado");
  };

  return (
    <div className="space-y-2">
      <p className="text-[12px] text-slate-500 dark:text-slate-400 ml-1 mb-3">
        Define el enfoque pastoral de cada día de la semana.
      </p>
      {DAY_KEYS.map((key) => {
        const isEditing = editingKey === key;
        const { bg, text } = DAY_COLORS[key];
        return (
          <div key={key} className="bg-white dark:bg-slate-800 rounded-[20px] border border-slate-200/50 dark:border-slate-700/40 shadow-[0_1px_6px_rgba(0,0,0,0.03)] px-4 py-3.5 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-[13px] flex items-center justify-center shrink-0 ${bg}`}>
              <span className={`text-[10px] font-extrabold tracking-wider ${text}`}>{DAY_ABBREV[key]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{DAY_LABELS[key]}</p>
              {isEditing ? (
                <input
                  autoFocus
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onBlur={() => commitEdit(key)}
                  onKeyDown={e => {
                    if (e.key === "Enter")  commitEdit(key);
                    if (e.key === "Escape") cancelEdit();
                  }}
                  placeholder="Actividad para este día..."
                  className="w-full bg-transparent text-[14px] font-medium text-slate-800 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 outline-none border-b border-slate-200 dark:border-slate-600 pb-0.5"
                />
              ) : (
                <p className="text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate">
                  {plan[key] || (
                    <span className="text-slate-300 dark:text-slate-500 italic font-normal">Sin asignar</span>
                  )}
                </p>
              )}
            </div>
            {isEditing ? (
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => commitEdit(key)}
                  className="h-8 w-8 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 active:scale-[0.93] transition-all duration-150"
                >
                  <Check size={13} />
                </button>
                <button
                  onClick={cancelEdit}
                  className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400 active:scale-[0.93] transition-all duration-150"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit(key)}
                className="h-8 w-8 rounded-full flex items-center justify-center text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-500 transition-colors duration-150 shrink-0"
              >
                <Pencil size={13} />
              </button>
            )}
          </div>
        );
      })}
      <button
        onClick={handleReset}
        className="w-full flex items-center justify-center gap-2 py-3 mt-1 text-[12px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-150"
      >
        <RotateCcw size={12} />
        Restaurar valores por defecto
      </button>
    </div>
  );
}

// ── Segment tabs ──────────────────────────────────────────────────────────────
const MAIN_TABS = [
  { id: "week",  label: "Mi semana"      },
  { id: "daily", label: "Horario diario" },
];

// ── Exported content component ────────────────────────────────────────────────
export function SettingsPlanContent({ confirm: externalConfirm }) {
  const [tab, setTab] = useState("week");
  const [internalConfirm, InternalConfirmUI] = useConfirm();
  const confirm = externalConfirm ?? internalConfirm;

  return (
    <>
      {!externalConfirm && InternalConfirmUI}
      <div className="px-4 pt-4 pb-6 space-y-4">
        {/* Segment control */}
        <div className="flex gap-1 bg-slate-200/50 dark:bg-slate-700/40 backdrop-blur-xl rounded-full p-1 border border-slate-200/30 dark:border-slate-700/30">
          {MAIN_TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 py-2 rounded-[20px] text-[12px] font-semibold transition-all duration-200 ease-out active:scale-[0.98]
                ${tab === id
                  ? "bg-white/90 dark:bg-slate-800 text-[#1b4f9c] dark:text-blue-400 shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "week"  && <WeekSection  confirm={confirm} />}
        {tab === "daily" && <DailySection confirm={confirm} />}
      </div>
    </>
  );
}

export default function SettingsPlan() {
  return (
    <div className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))] font-sans">
      <TopBar title="Horario pastoral" safeFallback="/settings" />
      <SettingsPlanContent />
    </div>
  );
}
